import { useState, useEffect, useRef, useCallback } from 'react';
import { X, Send, Sparkles, Trash2, CreditCard, Loader2, Check, ChevronRight, Zap, ShoppingCart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import api from '@/lib/api';

export function BackstagePanel({ siteId, siteData, onApplyActions, isOpen, onClose }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [credits, setCredits] = useState(null);
  const [pendingActions, setPendingActions] = useState(null);
  const [showBuyCredits, setShowBuyCredits] = useState(false);
  const [creditPacks, setCreditPacks] = useState(null);
  const [purchasing, setPurchasing] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Fetch credits and history when panel opens
  useEffect(() => {
    if (isOpen && siteId) {
      fetchCredits();
      fetchHistory();
    }
  }, [isOpen, siteId]);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, pendingActions]);

  // Focus input when panel opens
  useEffect(() => {
    if (isOpen) setTimeout(() => inputRef.current?.focus(), 300);
  }, [isOpen]);

  const fetchCredits = async () => {
    try {
      const res = await api.get(`/ai/credits/${siteId}`);
      setCredits(res.data);
    } catch { /* ignore */ }
  };

  const fetchHistory = async () => {
    try {
      const res = await api.get(`/ai/history/${siteId}`);
      setMessages(res.data.messages || []);
    } catch { /* ignore */ }
  };

  const fetchCreditPacks = async () => {
    try {
      const res = await api.get('/ai/credit-packs');
      setCreditPacks(res.data.packs);
    } catch { /* ignore */ }
  };

  const handleBuyCredits = async (packId) => {
    setPurchasing(true);
    try {
      const res = await api.post('/ai/credits/purchase', {
        site_id: siteId,
        pack: packId,
        origin_url: window.location.origin,
      });
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start checkout');
    } finally {
      setPurchasing(false);
    }
  };

  // Check for returning from Stripe credit purchase
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    const purchased = params.get('credits_purchased');
    if (purchased && sessionId) {
      const pollStatus = async (attempts = 0) => {
        if (attempts >= 5) return;
        try {
          const res = await api.get(`/ai/credits/purchase-status/${sessionId}`);
          if (res.data.payment_status === 'paid') {
            toast.success(`${res.data.credits_added} credits added!`);
            fetchCredits();
            // Clean URL
            window.history.replaceState({}, '', window.location.pathname);
            return;
          }
        } catch { /* ignore */ }
        setTimeout(() => pollStatus(attempts + 1), 2000);
      };
      pollStatus();
    }
  }, [siteId]);

  const handleSend = async () => {
    const text = input.trim();
    if (!text || isLoading) return;
    if (credits && credits.balance <= 0) {
      toast.error('No credits remaining. Ask your admin to top up.');
      return;
    }

    setInput('');
    // Optimistic user message
    const userMsg = { role: 'user', content: text, id: Date.now().toString() };
    setMessages(prev => [...prev, userMsg]);
    setIsLoading(true);

    try {
      const res = await api.post('/ai/chat', { site_id: siteId, message: text });
      const assistantMsg = {
        role: 'assistant',
        content: res.data.message,
        actions: res.data.actions || [],
        id: (Date.now() + 1).toString(),
      };
      setMessages(prev => [...prev, assistantMsg]);
      setCredits(prev => prev ? { ...prev, balance: res.data.credits_remaining } : prev);

      // If there are actions, set them as pending for user approval
      if (res.data.actions?.length > 0) {
        setPendingActions({ msgId: assistantMsg.id, actions: res.data.actions });
      }
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to get response';
      if (err.response?.status === 402) {
        setMessages(prev => [...prev, {
          role: 'assistant', content: 'You\'ve run out of Backstage credits. Please contact your admin to top up.',
          id: (Date.now() + 1).toString(),
        }]);
      } else {
        toast.error(detail);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleApplyActions = useCallback(() => {
    if (!pendingActions) return;
    onApplyActions(pendingActions.actions);
    setPendingActions(null);
    toast.success('Changes applied! Remember to save.');
  }, [pendingActions, onApplyActions]);

  const handleDismissActions = () => setPendingActions(null);

  const handleClearHistory = async () => {
    try {
      await api.delete(`/ai/history/${siteId}`);
      setMessages([]);
      setPendingActions(null);
      toast.success('Chat cleared');
    } catch {
      toast.error('Failed to clear history');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Quick action suggestions
  const suggestions = [
    "Write me a catchy headline",
    "Add a new upcoming event",
    "Improve my about section",
    "What content am I missing?",
  ];

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40" onClick={onClose} />
      )}

      {/* Panel */}
      <div
        className={cn(
          "fixed top-0 right-0 h-full w-full sm:w-[420px] bg-slate-900 border-l border-slate-700/50 z-50 flex flex-col transition-transform duration-300 ease-out",
          isOpen ? "translate-x-0" : "translate-x-full"
        )}
        data-testid="backstage-panel"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-700/50 bg-slate-800/50 backdrop-blur-xl">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-lg shadow-orange-500/20">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-base font-bold text-white leading-tight">Backstage</h3>
              <p className="text-xs text-slate-400">AI Assistant</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {credits && (
              <button
                onClick={() => { setShowBuyCredits(!showBuyCredits); if (!creditPacks) fetchCreditPacks(); }}
                className="flex items-center gap-1.5 bg-slate-700/50 hover:bg-slate-600/50 px-3 py-1.5 rounded-full transition-colors cursor-pointer"
                data-testid="credits-display"
              >
                <Zap className="w-3.5 h-3.5 text-amber-400" />
                <span className="text-xs font-semibold text-amber-300">{credits.balance}</span>
              </button>
            )}
            <button
              onClick={handleClearHistory}
              className="p-2 text-slate-400 hover:text-white hover:bg-slate-700/50 rounded-lg transition-colors"
              title="Clear chat"
              data-testid="clear-chat-btn"
            >
              <Trash2 className="w-4 h-4" />
            </button>
            <button
              onClick={onClose}
              className="p-2 text-slate-400 hover:text-white hover:bg-slate-700/50 rounded-lg transition-colors"
              data-testid="close-backstage-btn"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {messages.length === 0 && !isLoading && (
            <div className="flex flex-col items-center justify-center h-full text-center py-8">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-500/20 to-orange-600/20 flex items-center justify-center mb-4">
                <Sparkles className="w-8 h-8 text-amber-400" />
              </div>
              <h4 className="text-lg font-semibold text-white mb-2">Welcome to Backstage</h4>
              <p className="text-sm text-slate-400 mb-6 max-w-xs">
                I can help you manage your site content. Try asking me to update text, add events, or improve your copy.
              </p>
              <div className="flex flex-wrap gap-2 justify-center">
                {suggestions.map((s) => (
                  <button
                    key={s}
                    onClick={() => { setInput(s); inputRef.current?.focus(); }}
                    className="text-xs bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white px-3 py-2 rounded-full border border-slate-700 hover:border-slate-600 transition-all"
                    data-testid="suggestion-btn"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((msg) => (
            <div
              key={msg.id}
              className={cn(
                "flex",
                msg.role === 'user' ? 'justify-end' : 'justify-start'
              )}
            >
              <div
                className={cn(
                  "max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed",
                  msg.role === 'user'
                    ? 'bg-orange-600 text-white rounded-br-md'
                    : 'bg-slate-800 text-slate-200 border border-slate-700/50 rounded-bl-md'
                )}
                data-testid={msg.role === 'user' ? 'user-message' : 'assistant-message'}
              >
                <p className="whitespace-pre-wrap">{msg.content}</p>
                {msg.actions?.length > 0 && (
                  <div className="mt-2 pt-2 border-t border-slate-600/50">
                    <p className="text-xs text-amber-300 font-medium flex items-center gap-1">
                      <Zap className="w-3 h-3" /> {msg.actions.length} action{msg.actions.length > 1 ? 's' : ''} suggested
                    </p>
                  </div>
                )}
              </div>
            </div>
          ))}

          {/* Loading indicator */}
          {isLoading && (
            <div className="flex justify-start">
              <div className="bg-slate-800 border border-slate-700/50 rounded-2xl rounded-bl-md px-4 py-3">
                <div className="flex items-center gap-2">
                  <div className="flex gap-1">
                    <span className="w-2 h-2 bg-amber-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-2 h-2 bg-amber-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-2 h-2 bg-amber-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                  <span className="text-xs text-slate-400">Thinking...</span>
                </div>
              </div>
            </div>
          )}

          {/* Pending actions bar */}
          {pendingActions && (
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4" data-testid="pending-actions">
              <p className="text-sm font-medium text-amber-300 mb-3 flex items-center gap-2">
                <Sparkles className="w-4 h-4" />
                Apply {pendingActions.actions.length} change{pendingActions.actions.length > 1 ? 's' : ''} to your site?
              </p>
              <div className="space-y-1.5 mb-3">
                {pendingActions.actions.map((action, i) => (
                  <div key={i} className="text-xs text-slate-300 flex items-start gap-2">
                    <ChevronRight className="w-3 h-3 text-amber-400 mt-0.5 flex-shrink-0" />
                    <span>
                      {action.type === 'update_field' && `Update ${action.module}.${action.field}`}
                      {action.type === 'add_item' && `Add item to ${action.module}`}
                      {action.type === 'remove_item' && `Remove item from ${action.module}`}
                    </span>
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={handleApplyActions}
                  size="sm"
                  className="bg-amber-500 hover:bg-amber-600 text-black font-semibold text-xs"
                  data-testid="apply-actions-btn"
                >
                  <Check className="w-3.5 h-3.5 mr-1" /> Apply
                </Button>
                <Button
                  onClick={handleDismissActions}
                  variant="ghost"
                  size="sm"
                  className="text-slate-400 hover:text-white text-xs"
                  data-testid="dismiss-actions-btn"
                >
                  Dismiss
                </Button>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Buy Credits Panel */}
        {showBuyCredits && creditPacks && (
          <div className="border-t border-slate-700/50 px-5 py-4 bg-slate-800/80 space-y-3" data-testid="buy-credits-panel">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold text-white flex items-center gap-2">
                <ShoppingCart className="w-4 h-4 text-amber-400" /> Buy Credits
              </p>
              <button onClick={() => setShowBuyCredits(false)} className="text-slate-400 hover:text-white">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {Object.entries(creditPacks).map(([id, pack]) => (
                <button
                  key={id}
                  onClick={() => handleBuyCredits(id)}
                  disabled={purchasing}
                  className="bg-slate-700/50 hover:bg-slate-600/50 border border-slate-600 hover:border-amber-500/50 rounded-xl p-3 text-center transition-all group"
                  data-testid={`buy-pack-${id}`}
                >
                  <p className="text-lg font-bold text-amber-400 group-hover:text-amber-300">{pack.credits}</p>
                  <p className="text-[10px] text-slate-400 mb-1">credits</p>
                  <p className="text-xs font-semibold text-white">{pack.display}</p>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Input area */}
        <div className="border-t border-slate-700/50 p-4 bg-slate-800/30">
          {credits && credits.balance <= 0 ? (
            <div className="text-center py-3">
              <p className="text-sm text-slate-400 mb-2">No credits remaining</p>
              <Button
                onClick={() => { setShowBuyCredits(true); if (!creditPacks) fetchCreditPacks(); }}
                size="sm"
                className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-400 hover:to-orange-500 text-white"
                data-testid="buy-credits-empty-btn"
              >
                <ShoppingCart className="w-3.5 h-3.5 mr-1.5" /> Buy Credits
              </Button>
            </div>
          ) : (
            <div className="flex gap-2">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask Backstage anything..."
                rows={1}
                className="flex-1 bg-slate-800 border border-slate-600 rounded-xl px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-500/50 resize-none min-h-[44px] max-h-[120px]"
                data-testid="backstage-input"
                style={{ height: 'auto', overflow: 'hidden' }}
                onInput={(e) => { e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }}
              />
              <Button
                onClick={handleSend}
                disabled={!input.trim() || isLoading}
                className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-400 hover:to-orange-500 text-white rounded-xl px-4 h-[44px] self-end"
                data-testid="send-message-btn"
              >
                {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </Button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
